// V5.68 : "Cockpit de Sérénité" — Hub des 3 Sphères + Bot Sentinel + Badge Ambassadeur
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Bot, Sparkles, BookOpen, ShieldCheck } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import axios from 'axios';
import { API } from '../../../App';
import NextActionCard from '../NextActionCard';
import SavingsCounter from '../SavingsCounter';
import TimelineEvents from '../TimelineEvents';

// Score de Sérénité : statut admin → pourcentage
const SERENITY_SCORE = {
  nouveau: 20,
  details_submitted: 25,
  en_attente_documents: 25,
  en_cours: 40,
  en_cours_de_traitement: 40,
  resiliation_envoyee: 60,
  validation_resiliation: 80,
  termine: 100,
  TERMINE: 100,
  VALIDATED: 30,
  SIGNE: 30,
  annule: 0
};

const MISSION_META = {
  demenagement: {
    emoji: '🏠',
    label: 'Déménagement',
    color: '#00F5FF',       // cyan
    glow: 'rgba(0,245,255,0.4)',
    packTypes: ['mission_demenagement', 'nouveau_depart', 'demenagement']
  },
  succession: {
    emoji: '📜',
    label: 'Succession',
    color: '#FF66CC',       // magenta
    glow: 'rgba(255,102,204,0.4)',
    packTypes: ['mission_heritage', 'serenite_succession', 'succession']
  },
  audit: {
    emoji: '📊',
    label: 'Audit',
    color: '#00FF88',       // green
    glow: 'rgba(0,255,136,0.4)',
    packTypes: ['mission_audit', 'audit_pouvoir_achat', 'audit']
  }
};

// Composant Sphère individuelle
const SerenityOrb = ({ meta, score, active, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const filled = Math.min(score, 100);
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const strokeDash = (filled / 100) * circumference;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.06 }}
      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
      className="flex flex-col items-center cursor-pointer group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      data-testid={`serenity-orb-${meta.label.toLowerCase()}`}
    >
      {/* Sphère SVG */}
      <div className="relative w-36 h-36 mb-3">
        {/* Halo de glow */}
        <div
          className="absolute inset-0 rounded-full transition-all duration-500"
          style={{
            background: active
              ? `radial-gradient(circle at 35% 35%, ${meta.color}30, ${meta.color}08)`
              : 'radial-gradient(circle at 35% 35%, rgba(255,255,255,0.04), transparent)',
            boxShadow: hovered || active
              ? `0 0 40px ${meta.glow}, 0 0 80px ${meta.glow}40`
              : `0 0 20px ${meta.glow}30`
          }}
        />

        {/* Cercle SVG */}
        <svg className="w-full h-full -rotate-90" viewBox="0 0 128 128">
          {/* Fond */}
          <circle cx="64" cy="64" r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
          {/* Progression */}
          <circle
            cx="64" cy="64" r={radius}
            fill="none"
            stroke={meta.color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${strokeDash} ${circumference}`}
            style={{
              filter: `drop-shadow(0 0 6px ${meta.color})`,
              transition: 'stroke-dasharray 1.2s ease-in-out'
            }}
          />
        </svg>

        {/* Centre : emoji + % */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl mb-1">{meta.emoji}</span>
          <span
            className="text-lg font-orbitron font-bold"
            style={{ color: meta.color, textShadow: `0 0 10px ${meta.color}` }}
          >
            {filled}%
          </span>
        </div>
      </div>

      {/* Nom de mission */}
      <p className="font-orbitron text-sm font-bold text-white mb-1 text-center">{meta.label}</p>

      {/* Label selon score */}
      <p className="text-xs text-gray-500 text-center max-w-[120px]">
        {filled === 0 ? 'Pas encore démarrée' :
         filled < 30 ? 'En lancement' :
         filled < 60 ? 'En traitement' :
         filled < 90 ? 'Résiliation en cours' :
         'Mission accomplie !'}
      </p>

      {active && filled > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-2 flex items-center gap-1 text-xs font-mono"
          style={{ color: meta.color }}
        >
          Gérer <ArrowRight className="w-3 h-3" />
        </motion.div>
      )}
    </motion.div>
  );
};

export const OverviewTab = ({ user, activeSubscription, setActiveTab }) => {
  const [missions, setMissions] = useState([]);
  const [latestArticle, setLatestArticle] = useState(null);
  const [retentionStatus, setRetentionStatus] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/missions/active`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        setMissions(res.data.missions || []);
      } catch { /* silent */ }

      // Blog : dernier article publié (public, pas besoin de token)
      try {
        const blogRes = await axios.get(`${API}/blog/articles?limit=1`);
        setLatestArticle((blogRes.data.articles || [])[0] || null);
      } catch { /* silent */ }

      // Countdown Zéro Trace
      try {
        const token = localStorage.getItem('token');
        const retRes = await axios.get(`${API}/data-retention/status`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        setRetentionStatus(retRes.data);
      } catch { /* silent */ }
    };
    load();
  }, []);

  // Calculer le score de chaque type de mission (utilise admin_status = vraie source de vérité)
  const getScore = (packTypes) => {
    const mission = missions.find(m => packTypes.includes(m.pack_type));
    if (!mission) return 0;
    // V5.69 : Priorité à admin_status (statut réel DB) sur status (peut être "SIGNE")
    const statusKey = mission.admin_status || mission.status;
    return SERENITY_SCORE[statusKey] || 20;
  };

  const hasActiveMissions = missions.length > 0;

  return (
    <div className="space-y-8" data-testid="overview-tab">

      {/* Bienvenue */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-1">
          Bonjour, <span className="text-cyan-400">{user?.name?.split(' ')[0] || user?.email?.split('@')[0]}</span>
        </h2>
        <p className="text-gray-500 font-mono text-xs">// COCKPIT_DE_SÉRÉNITÉ</p>
      </motion.div>

      {/* Hub des 3 Sphères */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass-card p-6 border border-gray-700/60"
        data-testid="serenity-hub"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-orbitron text-base font-bold text-white">Hub de Sérénité</h3>
            <p className="text-gray-500 text-xs font-mono mt-0.5">Vos missions, visuellement</p>
          </div>
          {hasActiveMissions && (
            <button
              onClick={() => setActiveTab('missions')}
              className="text-cyan-400 text-xs font-mono flex items-center gap-1 hover:text-cyan-300 transition-colors"
              data-testid="missions-cta-hub"
            >
              Voir tout <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </div>

        <div className="flex items-center justify-around gap-4 flex-wrap">
          {Object.entries(MISSION_META).map(([key, meta]) => {
            const score = getScore(meta.packTypes);
            const isActive = missions.some(m => meta.packTypes.includes(m.pack_type));
            return (
              <SerenityOrb
                key={key}
                meta={meta}
                score={score}
                active={isActive}
                onClick={() => isActive && setActiveTab('missions')}
              />
            );
          })}
        </div>

        {!hasActiveMissions && (
          <div className="text-center mt-6 pt-4 border-t border-gray-800">
            <p className="text-gray-500 text-sm mb-3">Aucune mission active — démarrez pour voir les sphères s'animer</p>
            <Button
              onClick={() => setActiveTab('payment')}
              className="bg-cyan-400 text-black hover:bg-cyan-300 font-bold"
              data-testid="start-btn"
            >
              Démarrer une mission
            </Button>
          </div>
        )}
      </motion.div>

      {/* CCC.13 — Bloc accueil enrichi (Prochaine action + Économies + Timeline)
          DESCENDU sous le Hub (avant Badge Ambassadeur) avec style scan-laser cyber */}
      <NextActionCard
        missions={missions}
        setActiveTab={setActiveTab}
        onNavigate={(route) => { window.location.href = route; }}
      />
      <SavingsCounter missions={missions} setActiveTab={setActiveTab} />
      <TimelineEvents missions={missions} />

      {/* Badge Ambassadeur */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="glass-card p-5 border border-gray-700/60 hover:border-magenta-400/40 transition-colors group"
        data-testid="ambassador-badge"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-magenta-400/30 to-cyan-400/20 flex items-center justify-center flex-shrink-0 border border-magenta-400/30 group-hover:border-magenta-400/60 transition-colors">
            <span className="text-2xl">✦</span>
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold font-orbitron text-sm mb-1">Badge Ambassadeur</p>
            <p className="text-gray-400 text-sm leading-relaxed">
              Devenez l'Effaceur de vos proches — offrez-leur la sérénité.
            </p>
            <button
              onClick={() => setActiveTab('referral')}
              className="mt-2 text-cyan-400 text-xs font-mono flex items-center gap-1 hover:text-cyan-300 transition-colors"
              data-testid="go-to-referral-btn"
            >
              Inviter un ami <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="text-right flex-shrink-0">
            <Sparkles className="w-5 h-5 text-magenta-400 animate-pulse" />
          </div>
        </div>
      </motion.div>

      {/* Rappel Zéro Trace — si mission en cours avec countdown */}
      {retentionStatus?.has_active_mission && retentionStatus?.countdown_started && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="glass-card p-4 border border-cyan-400/30"
          data-testid="zero-trace-reminder"
        >
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-cyan-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-white text-sm font-semibold">Zéro Trace activé</p>
              <p className="text-gray-400 text-xs">
                Vos données sensibles seront automatiquement effacées dans{' '}
                <strong className="text-cyan-400">{retentionStatus.days_remaining ?? '—'} jours</strong>.
              </p>
            </div>
            <button
              onClick={() => setActiveTab('security')}
              className="text-cyan-400 text-xs font-mono hover:text-cyan-300"
            >
              Détails <ArrowRight className="w-3 h-3 inline" />
            </button>
          </div>
        </motion.div>
      )}

      {/* Blog teaser : dernier article publié */}
      {latestArticle && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28 }}
          className="glass-card p-5 border border-gray-700/60 hover:border-cyan-400/40 transition-colors group"
          data-testid="blog-teaser"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-cyan-400/10 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-6 h-6 text-cyan-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-gray-500 text-xs font-mono uppercase mb-0.5">Dernier guide publié</p>
              <p className="text-white text-sm font-semibold truncate">{latestArticle.title}</p>
              {latestArticle.meta_description && (
                <p className="text-gray-400 text-xs line-clamp-1">{latestArticle.meta_description}</p>
              )}
            </div>
            <Link
              to={`/blog/${latestArticle.slug}`}
              className="text-cyan-400 text-xs font-mono flex items-center gap-1 hover:text-cyan-300 flex-shrink-0"
            >
              Lire <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </motion.div>
      )}

      {/* CCC.13 — Sentinelle retirée (faisait doublon avec le toggle notifications + chatbot floating). */}
    </div>
  );
};
